'use client'

import Link from 'next/link'
import { AnimatePresence, motion } from 'motion/react'

import DynamicIcon from '@/components/DynamicIcon'
import { Kicker } from '@/components/Kicker'
import { CMSLink } from '@/components/Link'
import { Media } from '@/components/Media'
import { Button } from '@/components/ui/button'
import { useHeaderNavigationContext } from '@/providers/HeaderNavigation'
import { cn } from '@/utilities/ui'

import type { IconName } from '@/components/DynamicIcon'
import type { Header as HeaderType, Media as MediaType } from '@/payload-types'

const FEATURE_LINK_CLASSES =
  'flex items-center gap-2 font-body hover:opacity-100 rounded-lg group-hover/feature:opacity-50 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-1'

type FeatureLinks = NonNullable<NonNullable<HeaderType['dropdown']>['feature']>['links']
type FeatureLinkItem = Extract<FeatureLinks, { link: unknown }[]>[number]

const FeatureLinkContent: React.FC<{ item: FeatureLinkItem }> = ({ item }) => {
  const itemColor = item.color as
    | 'accent'
    | 'accent-2'
    | 'accent-3'
    | 'accent-4'
    | 'accent-5'
    | undefined

  const backgroundColor = itemColor
    ? `var(--${itemColor.replace('accent', 'accent-foreground')})`
    : undefined

  const textColor = itemColor ? `var(--${itemColor})` : undefined

  return (
    <>
      <div
        className="flex size-14 shrink-0 items-center justify-center gap-3 rounded-2xl bg-accent-foreground p-1.5 text-accent"
        style={{
          backgroundColor: backgroundColor,
          color: textColor,
        }}
      >
        <DynamicIcon name={(item.icon ?? 'link') as IconName} className="size-7" />
      </div>
      <div>
        <span className="mb-0.5 block font-body text-reg font-medium text-primary">
          {item.link?.label}
        </span>
        <span className="block font-body text-sm text-secondary-foreground/80">{item.subcopy}</span>
      </div>
    </>
  )
}

const CALLOUT_LINK_CLASSES =
  'no-underline outline-hidden transition-all duration-200 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-3 hover:opacity-80'

const Callout: React.FC<{
  callout: HeaderType['centerNav']['main'][number]['callout'] | HeaderType['dropdown']['callout']
}> = ({ callout }) => {
  const mainCalloutLink = callout?.links?.[0]?.link

  return (
    <motion.div
      initial={false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.1, ease: 'easeOut' }}
      className="flex flex-col gap-5"
    >
      {mainCalloutLink && mainCalloutLink.type === 'reference' ? (
        <CMSLink {...mainCalloutLink} label={null} className={CALLOUT_LINK_CLASSES}>
          {callout?.image && (
            <Media
              resource={callout?.image as MediaType}
              context={'thumbnail'}
              className="aspect-3/2 w-full overflow-hidden rounded-lg object-cover object-center"
              imgClassName="size-full rounded-lg object-cover object-center"
            />
          )}
          <p className="mt-2 line-clamp-3 h-[90px] overflow-hidden font-display text-2xl leading-tight text-balance text-ellipsis text-foreground">
            {callout?.title}
          </p>
        </CMSLink>
      ) : (
        <Link
          className={CALLOUT_LINK_CLASSES}
          href={mainCalloutLink?.url || '#'}
          target={mainCalloutLink?.newTab ? '_blank' : '_self'}
          rel={mainCalloutLink?.newTab ? 'noopener noreferrer' : undefined}
        >
          {callout?.image && (
            <Media
              resource={callout?.image as MediaType}
              className="aspect-3/2 w-full overflow-hidden rounded-lg object-cover object-center"
              imgClassName="size-full rounded-lg object-cover object-center"
            />
          )}
          <p className="mt-2 line-clamp-3 h-[90px] overflow-hidden font-display text-2xl leading-tight text-balance text-ellipsis text-foreground">
            {callout?.title}
          </p>
        </Link>
      )}
      <div className="mt-auto flex items-center justify-between gap-5">
        {callout?.links?.map((item) => (
          <Button
            key={item.id}
            variant="link"
            asChild
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-1"
          >
            {item.link.type === 'reference' ? (
              <CMSLink key={item.id} {...item.link} />
            ) : (
              <Link
                href={item.link.url || '#'}
                target={item.link.newTab ? '_blank' : '_self'}
                rel={item.link.newTab ? 'noopener noreferrer' : undefined}
              >
                {item.link.label}
              </Link>
            )}
          </Button>
        ))}
      </div>
    </motion.div>
  )
}

const HEIGHT_DURATION = 0.25
const FADE_IN_DURATION = 0.1
const FADE_OUT_DURATION = 0.05

export const NavDropdown: React.FC<{
  data: HeaderType
}> = ({ data }) => {
  const { isDropdownOpen, setIsDropdownOpen, dropdownRef, activeCallout } =
    useHeaderNavigationContext()
  const main = data?.centerNav.main
  const dropdown = data?.dropdown

  const callout = dropdown?.callout
  const feature = dropdown?.feature
  const secondary = dropdown?.secondary

  return (
    <motion.nav
      id="nav-dropdown"
      onHoverStart={() => setIsDropdownOpen(true)}
      onHoverEnd={() => setIsDropdownOpen(false)}
      initial={false}
      animate={
        isDropdownOpen
          ? { y: 0, maxHeight: '680px', visibility: 'visible', pointerEvents: 'auto' }
          : { y: -8, maxHeight: 0, visibility: 'hidden', pointerEvents: 'none' }
      }
      transition={{
        ease: 'easeOut',
        pointerEvents: {
          duration: 0,
        },
        y: {
          duration: HEIGHT_DURATION,
          delay: isDropdownOpen ? 0 : FADE_OUT_DURATION,
        },
        maxHeight: {
          duration: HEIGHT_DURATION,
          delay: isDropdownOpen ? 0 : FADE_OUT_DURATION,
        },
        visibility: {
          delay: isDropdownOpen ? 0 : FADE_OUT_DURATION + HEIGHT_DURATION,
        },
      }}
      className="nav-dropdown absolute top-15 wrapper hidden min-h-0 max-wrapper:px-0 lg:flex"
      ref={dropdownRef as React.RefObject<HTMLDivElement>}
      aria-hidden={!isDropdownOpen}
      aria-labelledby="nav-center"
      role="menu"
    >
      <motion.div
        initial={false}
        animate={
          isDropdownOpen
            ? {
                boxShadow: '0 28px 28px #0000001a, 0 8px 8px -4px #0000001a',
                borderColor: 'var(--border)',
                opacity: 1,
              }
            : { boxShadow: 'none', borderColor: 'hsl(0 0% 19% / 0)', opacity: 0 }
        }
        transition={{
          ease: 'easeOut',
          opacity: {
            duration: isDropdownOpen ? 0.1 : HEIGHT_DURATION - 0.05,
            delay: isDropdownOpen ? 0 : FADE_OUT_DURATION,
          },
        }}
        className="mt-2 rounded-2xl border bg-background"
      >
        <motion.div
          initial={false}
          animate={
            isDropdownOpen
              ? {
                  opacity: 1,
                }
              : { opacity: 0 }
          }
          transition={{
            ease: 'easeOut',
            opacity: {
              duration: isDropdownOpen ? FADE_IN_DURATION : FADE_OUT_DURATION,
              delay: isDropdownOpen ? HEIGHT_DURATION : 0,
            },
          }}
          className="grid w-full grid-cols-[minmax(200px,20rem)_1fr] gap-6 p-4 xl:gap-8"
        >
          {/* Callout column */}
          <AnimatePresence mode="wait">
            {activeCallout !== null && main?.[activeCallout]?.callout ? (
              <Callout key={activeCallout} callout={main?.[activeCallout]?.callout} />
            ) : (
              <Callout key={activeCallout} callout={callout as HeaderType['dropdown']['callout']} />
            )}
          </AnimatePresence>
          {/* Columns */}
          <div className="flex flex-col gap-6 p-6 pr-0">
            <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)] items-start gap-8 xl:gap-14">
              {/* First column - featured */}
              <div className="flex flex-col gap-4">
                <Kicker className="w-fit" size="small">
                  {feature?.title}
                </Kicker>
                <div className="group/feature flex flex-col gap-7">
                  {feature?.links?.map((item) =>
                    item.link.type === 'reference' ? (
                      <CMSLink
                        key={item.id}
                        {...item.link}
                        label={null}
                        className={FEATURE_LINK_CLASSES}
                      >
                        <FeatureLinkContent item={item} />
                      </CMSLink>
                    ) : (
                      <Link
                        key={item.id}
                        href={item.link.url || '#'}
                        target={item.link.newTab ? '_blank' : '_self'}
                        rel={item.link.newTab ? 'noopener noreferrer' : undefined}
                        className={FEATURE_LINK_CLASSES}
                      >
                        <FeatureLinkContent item={item} />
                      </Link>
                    ),
                  )}
                </div>
              </div>
              {/* Second & Third columns - secondary links */}
              {secondary?.map((column) => (
                <div key={column.id} className="group/secondary flex flex-col gap-3">
                  <Kicker className="w-fit" size="small">
                    {column.title}
                  </Kicker>
                  {column.links?.map((item) =>
                    item.link.type === 'reference' ? (
                      <CMSLink
                        key={item.id}
                        {...item.link}
                        label={null}
                        className={cn(
                          'rounded-lg font-body text-reg font-medium text-primary transition-all duration-200 group-hover/secondary:opacity-50 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-1',
                        )}
                      >
                        {item.link.label}
                      </CMSLink>
                    ) : (
                      <Link
                        key={item.id}
                        href={item.link.url || '#'}
                        target={item.link.newTab ? '_blank' : '_self'}
                        rel={item.link.newTab ? 'noopener noreferrer' : undefined}
                        className={cn(
                          'rounded-lg font-body text-reg font-medium text-primary transition-all duration-200 group-hover/secondary:opacity-50 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-1',
                        )}
                      >
                        {item.link.label}
                      </Link>
                    ),
                  )}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.nav>
  )
}
